import type { CodingAnswerOption } from '@seen/core/codings'
import { cn } from '@seen/ui'
import { IconCheck, IconSelector } from '@tabler/icons-react'
import { type JSX, type KeyboardEvent, useId, useState } from 'react'
import {
  Command,
  CommandEmpty,
  CommandInput,
  CommandItem,
  CommandList,
} from '#src/components/core/Command.tsx'
import { Popover } from '#src/components/core/popover/Popover.tsx'
import { PopoverContent } from '#src/components/core/popover/PopoverContent.tsx'
import { PopoverTrigger } from '#src/components/core/popover/PopoverTrigger.tsx'
import type { LastAnswer } from '#src/components/rich-text-editor/ObservationNode/panel/ChoiceMatrix.tsx'

export interface ChoiceSelectProps {
  label: string
  options: readonly CodingAnswerOption[]
  chosen: string | undefined
  // An earlier answer, offered while the question is unanswered.
  offer?: LastAnswer
  // `null` clears the answer.
  onAnswer: (option: CodingAnswerOption | null) => void
  disabled?: boolean
}

// A question whose answers are too many or too long to sit side by side:
// one trigger showing the answer, opening a searchable list. Picking the
// chosen answer again, or Backspace/Delete on the trigger, clears it.
export function ChoiceSelect({
  label,
  options,
  chosen,
  offer,
  onAnswer,
  disabled = false,
}: ChoiceSelectProps): JSX.Element {
  const [open, setOpenState] = useState(false)
  const [search, setSearch] = useState('')
  const setOpen = (next: boolean): void => {
    setOpenState(next)
    setSearch('')
  }
  const hintId = useId()
  const picked = options.find((option) => option.code === chosen)
  const priorHint = offer
    ? `Last answer${offer.day ? ` · ${offer.day}` : ''}: ${offer.option.display ?? offer.option.code} (${offer.source})`
    : undefined

  const onTriggerKeyDown = (event: KeyboardEvent<HTMLButtonElement>): void => {
    if ((event.key === 'Backspace' || event.key === 'Delete') && picked) {
      event.preventDefault()
      event.stopPropagation()
      onAnswer(null)
    }
  }

  return (
    <Popover open={!disabled && open} onOpenChange={setOpen} modal={false}>
      <PopoverTrigger asChild disabled={disabled}>
        <button
          type="button"
          role="combobox"
          aria-expanded={open}
          aria-label={`${label}: ${picked ? (picked.display ?? picked.code) : 'not answered'}`}
          aria-describedby={priorHint ? hintId : undefined}
          title={priorHint}
          data-prior-answer={priorHint ? '' : undefined}
          disabled={disabled}
          onKeyDown={onTriggerKeyDown}
          className={cn(
            'm-0.5 inline-flex min-h-6 w-full max-w-md items-center justify-between gap-2 rounded-md border border-gray-450 px-1.5 py-0.5 text-left text-xs leading-4 hover:bg-gray-460 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-zinc-950 disabled:cursor-not-allowed disabled:opacity-60',
            !picked && 'text-muted-foreground',
            priorHint &&
              'outline outline-1 outline-offset-2 outline-dashed outline-muted-foreground/40'
          )}
        >
          <span>
            {picked
              ? (picked.display ?? picked.code)
              : offer
                ? `Last: ${offer.option.display ?? offer.option.code}`
                : 'Choose…'}
          </span>
          <IconSelector className="size-3.5 shrink-0 opacity-60" />
        </button>
      </PopoverTrigger>
      {priorHint && (
        <span id={hintId} className="sr-only">
          {priorHint}
        </span>
      )}
      <PopoverContent align="start" className="w-[min(28rem,90vw)] p-0">
        <Command>
          <CommandInput
            value={search}
            onValueChange={setSearch}
            placeholder="Search answers…"
            className="rounded-none border-b px-3 text-xs"
          />
          <CommandList>
            <CommandEmpty>No answer matches.</CommandEmpty>
            {options.map((option) => {
              const text = option.display ?? option.code
              const isPicked = option.code === chosen
              return (
                <CommandItem
                  key={option.code}
                  // Matched on the answer as read, never its code.
                  value={text}
                  onSelect={() => {
                    setOpen(false)
                    onAnswer(isPicked ? null : option)
                  }}
                  className="gap-2 text-xs"
                >
                  <IconCheck
                    className={cn(
                      'size-3.5 shrink-0',
                      isPicked ? 'opacity-100' : 'opacity-0'
                    )}
                  />
                  <span className="flex-1">{text}</span>
                  {offer?.option.code === option.code && (
                    <span className="text-muted-foreground">Last</span>
                  )}
                </CommandItem>
              )
            })}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}
